// components/StepCard.js
"use client";
import React, { useRef } from "react";

/**
 * StepCard
 * - card for each step on timeline
 * - props:
 *    step: { id, title, date, details, icon?, image? }
 *    isActive: boolean
 *    isExpanded: boolean
 *    onToggle: (id) => void
 *    onFocusSelect: (id) => void
 *    theme: { primary, accent }
 *
 * Accessibility: button with aria-expanded, keyboard support.
 */

export default function StepCard({ step, isActive, isExpanded, onToggle, onFocusSelect, theme = { primary: "#7c3aed", accent: "#06b6d4" } }) {
  const containerRef = useRef(null);

  const handleClick = () => {
    onFocusSelect?.(step.id);
    onToggle?.(step.id);
    // scroll into center
    setTimeout(() => {
      try {
        containerRef.current?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      } catch (e) {
        // fallback ignore
      }
    }, 80);
  };

  // gradients for neon ring using theme colors
  const ringGradient = `conic-gradient(from 0deg, ${theme.primary}, ${theme.accent}, #3b82f6, ${theme.primary})`;
  const thinGradient = `conic-gradient(from 0deg, ${theme.primary}, ${theme.accent})`;

  return (
    <div ref={containerRef} className="relative flex flex-col items-center" dir="rtl">
      {/* Neon ring blurred (behind) */}
      <span
        aria-hidden
        className={`
          absolute -inset-[6px] rounded-2xl pointer-events-none transform-gpu
          ${isActive ? "opacity-100" : "opacity-60"}
          transition-opacity duration-500
          hidden md:block
        `}
        style={{
          background: ringGradient,
          filter: "blur(10px)",
          zIndex: 1,
          mixBlendMode: "screen",
          animation: "spin 10s linear infinite",
        }}
      />

      {/* Thin ring (visible border that rotates) */}
      

      {/* actual card button */}
      <button
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
        aria-expanded={!!isExpanded}
        aria-controls={`step-panel-${step.id}`}
        className={`relative z-30 w-40 md:w-44 lg:w-48 h-36 md:h-40 rounded-2xl overflow-hidden focus:outline-none focus-visible:ring-4 focus-visible:ring-opacity-60
          transform transition-all duration-300
          ${isActive ? "scale-[1.04]" : "hover:scale-[1.02]"}
          bg-gradient-to-b from-[#071018] to-[#04060a] shadow-xl`}
      >
        {/* top visual area (badge) */}
        <div className="h-16 md:h-18 flex items-center justify-center transition-all">
          <div className="transition-all w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-tr from-[#7c3aed] to-[#06b6d4] flex items-center justify-center text-white font-bold shadow">
            {step.id}
          </div>
        </div>

        {/* title */}
        <div className="p-3 text-center">
          <div className="text-xs  font-extrabold text-white leading-tight">{step.title}</div>
          {step.date && <div className="text-xs text-slate-400 mt-1">{step.date}</div>}
          {isActive && <div className="mt-2 text-xs text-emerald-300 font-semibold">مرحلهٔ جاری</div>}
        </div>
      </button>

      {/* Expanded details (desktop below card, mobile handled in parent stacked) */}
      {isExpanded && (
        <div id={`step-panel-${step.id}`} className="mt-4 w-[320px] md:w-[360px] bg-[#071018] border border-slate-800/30 rounded-2xl p-4 text-sm text-slate-200 shadow-lg z-40">
          <div className="font-semibold text-white text-center mb-2">{step.title}</div>
          {step.date && <div className="text-xs text-slate-400 text-center mb-2">{step.date}</div>}
          <div className="leading-relaxed text-slate-300">{step.details ?? "جزئیات موجود نیست."}</div>
        </div>
      )}
    </div>
  );
}
